import { apiRequest } from "../utils/useAPIRequest";
import constant from "../utils/constant";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { queryClient } from "../App";
import { encodeQueryData } from "../utils/utils";
import { adminStore } from "../stores";

const useGetCompetitions = (
  competitionsTableQuery,
  filterQuery,
  searchQuery
) => {
  const query = useMemo(() => {
    if (searchQuery && filterQuery) {
      return `${competitionsTableQuery}&${filterQuery}&search=${searchQuery}`;
    } else if (searchQuery) {
      return `${competitionsTableQuery}&search=${searchQuery}`;
    } else if (filterQuery) {
      return `${competitionsTableQuery}&${filterQuery}`;
    } else {
      return competitionsTableQuery;
    }
  }, [filterQuery, competitionsTableQuery, searchQuery]);

  return useQuery({
    queryKey: ["getCompetitions", query],
    queryFn: () => {
      return apiRequest({
        url: `/competition/competitions?${query}`,
        method: "get",
      });
    },
    keepPreviousData: true,
  });
};

const useGetParticipants = (participantsTableQuery, searchQuery) => {
  const encodedQuery = encodeQueryData({
    ...participantsTableQuery,
  });

  return useQuery({
    queryKey: ["getParticipants", encodedQuery],
    queryFn: () => {
      return apiRequest({
        url: `/competition/admin/participants?${encodedQuery}`,
        method: "get",
      });
    },
    keepPreviousData: true,
  });
};
const useGetVotes = (votesTableQuery, searchQuery) => {
  const encodedQuery = encodeQueryData({
    ...votesTableQuery,
  });

  return useQuery({
    queryKey: ["getVotes", encodedQuery],
    queryFn: () => {
      return apiRequest({
        url: `/competition/admin/votes?${encodedQuery}`,
        method: "get",
      });
    },
    keepPreviousData: true,
  });
};

const createCompetition = async (payload, queryParams) => {
  const { status, errorMessage, data } = await apiRequest({
    url: "/competition",
    method: "post",
    payload,
  });
  if (status === constant.Success) {
    queryClient.invalidateQueries({
      queryKey: ["getCompetitions"],
    });
    return { status: constant.Success, data };
  }
  return { errorMessage, status, data };
};

const createVotes = async (payload) => {
  const { status, errorMessage, data } = await apiRequest({
    url: "/competition/create-vote-by-admin",
    method: "post",
    payload,
  });
  if (status === constant.Success) {
    queryClient.invalidateQueries({
      queryKey: ["getParticipants"],
    });
    return { status: constant.Success, data };
  }
  return { errorMessage, status, data };
};

const editCompetition = async (payload, competitionId) => {
  const { status, errorMessage, data } = await apiRequest({
    url: `/competition?id=${competitionId}`,
    method: "patch",
    payload,
  });
  if (status === constant.Success) {
    queryClient.invalidateQueries({
      queryKey: ["getCompetitions"],
    });
    return { status: constant.Success, data };
  }
  return { errorMessage, status, data };
};

const upgradeParticipants = async (payload) => {
  const { status, errorMessage, data } = await apiRequest({
    url: `/competition/upgrade-particiapants`,
    method: "patch",
    payload,
  });
  if (status === constant.Success) {
    queryClient.invalidateQueries({
      queryKey: ["getParticipants"],
    });
    return { status: constant.Success, data };
  }
  return { errorMessage, status, data };
};

const getCompetitions = async () => {
  const { status, data, errorMessage } = await apiRequest({
    url: "/competition/competitions",
    method: "get",
  });

  if (status === constant.Success) {
    const competitions = data?.competitions?.map((x) => ({
      value: x.name,
      key: x._id,
    }));
    adminStore.setCompetitions(competitions);
    return { status: constant.Success };
  }

  return { errorMessage, status };
};

export default {
  useGetCompetitions,
  createCompetition,
  editCompetition,
  useGetVotes,
  useGetParticipants,
  getCompetitions,
  upgradeParticipants,
  createVotes,
};
